import 'classlist-polyfill'
import 'promise-polyfill/src/polyfill'

import React, { FC, useState, useEffect } from 'react'
import { TeamReportWorkingTime, ReportTime } from '../../../states/types/team'
import Chart from "react-apexcharts"
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { format, combineYearMonthAndDay, diffDays, dateManipulateDay } from '../../../utils/date'

import Loading from '../common/Loading'

import { chartOptions } from './VariablesWorkTime'
import { LangValue } from '../../../states/types/common'
import { ApexOptions } from 'apexcharts'
import InfoTips from '../common/InfoTips'

interface WorkingTimeProps {
  isWorkingTimeLoading: boolean | undefined
  date: string | {start_date: string, end_date: string}
  workingTime: TeamReportWorkingTime | undefined
  mode: 'daily' | 'monthly' | 'custom'
}

interface ChartSeries {
  name: string
  data: number[]
}

const WorkingTime: FC<WorkingTimeProps> = (props) => {
  const { t } = useTranslation()
  const { language } = useParams<{ language: LangValue }>()

  const {
    isWorkingTimeLoading,
    date,
    workingTime,
    mode,
  } = props

  const [chartSeries, setChartSeries] = useState<ChartSeries[]>([{ name: "", data: [] }])

  const buildPositiveNegativeIcon = (value: number) => (value >= 0 ? "/static/images/report/icon_up.svg" : "/static/images/report/icon_down.svg")

  useEffect(() => {
    const target = workingTime?.target ? workingTime.target.time : 0
    const past = workingTime?.past ? workingTime.past.time : 0
    setChartSeries([{
      name: "",
      data: [target, past],
    }])
  }, [workingTime])

  const renderCompareTime = () => {
    if (!workingTime?.compare) {
      return (<div>---</div>)
    }

    let color = ""
    let mark = ""
    if (0 <= workingTime.compare.time) {
      color = "#32AA4C"
      mark = "+"
    } else {
      color = "#DD4A38"
      mark = "-"
    }

    return (
      <>
        <div style={{ color: color, display: "table" }}>
          <img className="positive_negative_icon" src={buildPositiveNegativeIcon(workingTime.compare.time)} alt="" />
          <span style={{ fontSize: "28px", fontWeight: "bold", verticalAlign: "middle" }}>{mark}</span>
          <span style={{ fontSize: "28px", fontWeight: "bold", verticalAlign: "middle" }}>
            {workingTime.compare.hour}
            :
            {(`00${String(workingTime.compare.minute)}`).slice(-2)}
          </span>
        </div>
      </>
    )
  }

  const renderWorkingTimeLabel = (color: string, target: ReportTime | undefined) => {
    if (!target) {
      return (
        <>---</>
      )
    }
    return (
      <>
        <span style={{ fontSize: "24px", fontWeight: "bold", color: color }}>
          {target.hour}
          :
          {(`00${String(target.minute)}`).slice(-2)}
        </span>
      </>
    )
  }

  const calcWorkingTimeMargin = () => (!workingTime?.target ? "1.25em" : "0.5em")

  const presentAverageText = (type: string) => {
    let dateString = ""
    if (type === "daily") {
      dateString = format(date, t('time.date.formats.with_day'), language)
    } else if (type === "monthly" && typeof date === 'string') {
      dateString = combineYearMonthAndDay(date, 1, t('time.date.formats.only_month'), language)
    } else if (type === "custom" && typeof date !== 'string') {
      // eslint-disable-next-line max-len
      dateString = `${format(date.start_date, t('time.date.formats.with_day'), language)} ~ ${format(date.end_date, t('time.date.formats.with_day'), language)}`
    }
    return (
      t("parts.working_time.average", { date: dateString })
    )
  }

  const pastAverageText = (type: string) => {
    if (type === "daily") {
      return t("parts.working_time.past")
    }
    if (type === "monthly" && typeof date === 'string') {
      return t("parts.working_time.last_month")
    }
    if (type === "custom" && typeof date !== 'string') {
      const diffCount = (diffDays(date.start_date, date.end_date) * -1) - 1
      const pastStartDate = format(dateManipulateDay(date.start_date, diffCount), t('time.date.formats.with_day'), language)
      const pastEndDate = format(dateManipulateDay(date.end_date, diffCount), t('time.date.formats.with_day'), language)
      return (
        t("parts.working_time.average", { date: `${pastStartDate} ~ ${pastEndDate}` })
      )
    }
    return ""
  }

  return (
    <div style={{ marginLeft: "0.5em", marginRight: "0.5em" }}>
      <div className="box">
        <div style={{ textAlign: "center", borderBottom: "1px solid rgba(51, 51, 51, 0.1)", padding: "1em" }}>
          <span style={{ fontWeight: "bold", fontSize: "24px", color: "#094E8D" }}>{t("parts.working_time.title")}</span>
        </div>
        <InfoTips>
          <>
            <div>
              チームメンバーの平均勤務時間と過去の平均勤務時間を表示しています
            </div>
          </>
        </InfoTips>
        {
          isWorkingTimeLoading || !workingTime || !chartSeries ? (
            <Loading />
          ) : (
            <>
              <div className="flex center">
                <div style={{
                  borderRight: "1px solid rgba(51, 51, 51, 0.1)",
                  padding: "40px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
                >
                  <div>
                    {
                      mode === "monthly" ? (
                        t("parts.working_time.comparison_last_month")
                      ) : (
                        t("parts.working_time.comparison_past")
                      )
                    }
                  </div>
                  {
                    renderCompareTime()
                  }
                </div>
                <div className="flex center" style={{ background: "rgba(51,51,51,0.05)", flex: 1 }}>
                  <div style={{ marginLeft: "1em", marginBottom: "0em", flex: 1 }}>
                    <div style={{ marginTop: "0em" }}>
                      <span style={{ fontSize: "12px", color: "#094E8D" }}>
                        {presentAverageText(mode)}
                      </span>
                    </div>
                    <div>
                      {
                        renderWorkingTimeLabel("#094E8D", workingTime?.target)
                      }
                    </div>
                    <div style={{ marginTop: calcWorkingTimeMargin() }}>
                      <span style={{ fontSize: "12px" }}>
                        {pastAverageText(mode)}
                      </span>
                    </div>
                    <div>
                      {
                        renderWorkingTimeLabel("rgba(51,51,51,0.9)", workingTime?.past)
                      }
                    </div>
                  </div>
                  <div style={{ flex: 8 }}>
                    <Chart
                      options={chartOptions as ApexOptions}
                      type="bar"
                      series={chartSeries}
                      height="185px"
                    />
                  </div>
                </div>
              </div>
            </>
          )
        }
      </div>
    </div>
  )
}

export default WorkingTime
